

import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { filter, map, mergeMap, share, skipWhile, takeWhile } from 'rxjs/operators';

import { Account, SiteConfig, Token, User, ExternalToolbarConfig } from './models';
import { StorageService } from './storage.service';
import { NavigationEnd, Router } from '@angular/router';

@Injectable()
export class SessionService {
  private _account: BehaviorSubject<Account> = new BehaviorSubject<Account>(null);
  private _isLandingPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isCsr: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _siteConfig: BehaviorSubject<SiteConfig> = new BehaviorSubject<SiteConfig>(null);
  private _externalToolbarConfig: BehaviorSubject<ExternalToolbarConfig> = new BehaviorSubject<ExternalToolbarConfig>(null);
  private _token: BehaviorSubject<Token> = new BehaviorSubject<Token>(null);
  private _user: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  private _locale = '';
  private _lock: 'site' | 'account';
  private _redirectUrl: string;
  public isLoggingOut = false;

  constructor(private storage: StorageService, private titleService: Title, private router: Router) {
    this.setSiteConfig(storage.get<SiteConfig>('siteConfig'), 'account');
    this.setToken(storage.get<Token>('token'));
    this.setUser(storage.get<User>('user'));
    this.setAccount(storage.get<Account>('account'));
    this.setIsCsr(storage.get<boolean>('isCsr'));
    this.setExternalToolBarConfig(storage.get<ExternalToolbarConfig>('externalToolbarConfig'));
    const storedLocale = storage.get<string>('locale');
    this.locale = storedLocale ? storedLocale : this.getBrowserLocale();
    this.initializeLandingPageTracking();
  }

  get isLandingPage(): Observable<boolean> {
    return this._isLandingPage.asObservable()
      .pipe(
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  get hasSession(): Observable<boolean> {
    return combineLatest([
      this._user.asObservable(),
      this._token.asObservable(),
      this._account.asObservable()
    ]).pipe(
      map(([user, token, account]) => !!user && !!token && !!account)
    );
  }

  get token(): Observable<Token> {
    return this._token.asObservable()
      .pipe(
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  public setToken(aToken: Token) {
    this.storage.set('token', aToken);
    this._token.next(aToken);
  }

  get account(): Observable<Account> {
    return this._account.asObservable()
      .pipe(
        share(),
        skipWhile(x => !x),
        skipWhile(x => this.isLoggingOut)
      );
  }

  get isCsr(): Observable<boolean> {
    return this._isCsr.asObservable()
      .pipe(
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  public setAccount(account: Account) {
    this.storage.set('account', account);

    if (!account) {
      this._account.next(account);
      return;
    }

    account.hasFeature = (feature: string) => account.features ? account.features.indexOf(feature) > -1 : false;
    account.hasAnyFeature = (features: string[]) => account.features && features ? account.features.some(x => features.some(y => y === x)) : false;

    this._account.next(account);
    this.setSiteConfig(account.siteConfig, 'account');
  }

  public setIsCsr(isCsr: boolean) {
    this.storage.set('isCsr', isCsr);
    this._isCsr.next(isCsr);
  }

  get siteConfig(): Observable<SiteConfig> {
    return this._siteConfig.asObservable()
      .pipe(
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  public setSiteConfig(siteConfig: SiteConfig, lock: 'site' | 'account') {
    if (siteConfig && this._lock === 'account' && lock === 'site') {
      return;
    }
    this.storage.set('siteConfig', siteConfig);

    this._siteConfig.next(siteConfig);

    if (siteConfig) {
      this._lock = lock;
      this.style(siteConfig.css);
    }

    // If the title is not hidden through the hidekeys then set it to partnerName
    const pageTitleHideKey = 'PAGE-TITLE';
    if (siteConfig && !siteConfig.hiddenContent.some(x => x.toUpperCase() === pageTitleHideKey)) {
      this.titleService.setTitle(siteConfig.partnerName);
    }
  }

  public setExternalToolBarConfig(externalToolbarConfig: ExternalToolbarConfig) {
    this.storage.set('externalToolbarConfig', externalToolbarConfig);
    this._externalToolbarConfig.next(externalToolbarConfig);
  }

  get externalToolbarConfig(): Observable<ExternalToolbarConfig> {
    return this._externalToolbarConfig.asObservable()
      .pipe(
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  get user(): Observable<any> {
    return this._user.asObservable()
      .pipe(
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  public setUser(user: any) {
    this.storage.set('user', user);
    this._user.next(user);
  }

  public isAuthorized(): Observable<boolean> {
    return this._token
      .pipe(
        map(x => !!x),
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  public isNotAuthorized(): Observable<boolean> {
    return this._token
      .pipe(
        map(x => !x),
        share(),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  public isReady(): Observable<boolean> {
    return this.isAuthorized()
      .pipe(
        mergeMap(x => {
          if (!x) {
            return of(false);
          }

          return this._account.pipe(
            map(y => !!y)
          );
        }),
        takeWhile(x => !this.isLoggingOut)
      );
  }

  get locale(): string {
    return this._locale;
  }

  set locale(value: string) {
    this.storage.set('locale', value);
    this._locale = value;
  }

  get redirectUrl(): string {
    return this._redirectUrl;
  }

  set redirectUrl(value: string) {
    this._redirectUrl = value;
  }

  public clearRedirectUrl() {
    this.redirectUrl = null;
  }

  public clear(): void {
    this._lock = null;
    this.storage.clear();
    this._token.next(null);
    this._user.next(null);
    this._account.next({} as Account);
    this._isCsr.next(null);
    this._siteConfig.next(null);
    this._externalToolbarConfig.next(null);
    this._locale = this.getBrowserLocale();
  }

  private style(css: string) {
    const elementId = 'custom-css';
    const oldStyleElement = document.getElementById(elementId);
    if (oldStyleElement) {
      document.body.removeChild(oldStyleElement);
    }
    const newStyleElement = document.createElement('style');
    newStyleElement.id = elementId;
    newStyleElement.innerHTML = css;
    document.body.appendChild(newStyleElement);
  }

  private getBrowserLocale(): string {
    // the default chrome locale is en-US, but our english translations are under 'en'
    // this was causing NotFound errors when trying to load en-US translations
    return navigator.language === 'en-US' ? 'en' : navigator.language;
  }

  private initializeLandingPageTracking(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncLandingPageWithRoute();
      });
  }

  private syncLandingPageWithRoute(): void {
    const tree = this.router.parseUrl(this.router.url);

    if ('isLandingPage' in tree.queryParams) {
      this._isLandingPage.next(tree.queryParams['isLandingPage'] === 'true');
      return;
    }

    let route = this.router.routerState.snapshot.root;

    while (route) {
      if (route.data?.['isLandingPage'] === true) {
        this._isLandingPage.next(true);
        return;
      }
      route = route.firstChild;
    }

    this._isLandingPage.next(false);
  }
}
